import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { listAssessments } from '@/api/assessments/api';
import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { deriveAssessmentFlowStep } from '@/utils/assessmentFlowRoute';

export const TOTAL_STEPS = 2;

/** View model for a single assessment row in the assessments list UI. */
export interface AssessmentRowViewModel {
  assessmentId: string;
  title: string;
  /** Formatted date string e.g. "Apr 10, 2026" — component prepends "Started " */
  startedLabel: string;
  lastModifiedBy: string | null;
  status: string;
  completionPct: number;
  currentStep: number;
  totalSteps: number;
  /** True when step 2 artifact has been approved — assessment is fully complete. */
  isApproved: boolean;
  /** Derived route target — use this instead of currentStep when opening an assessment. */
  flowStep: AssessmentFlowStep;
}

/** Return type of `useAssessmentList`. */
export interface UseAssessmentListResult {
  items: AssessmentRowViewModel[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads and maps the assessment list for the given project on mount, re-fetching when any
 * parameter changes and cancelling in-flight requests on unmount.
 *
 * @returns Mapped assessment rows plus loading and error state.
 */
export function useAssessmentList(
  accessToken: string,
  clientId: string,
  projectId: string,
): UseAssessmentListResult {
  const [items, setItems] = useState<AssessmentRowViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await listAssessments(accessToken, clientId, projectId);
        if (cancelled) {
          return;
        }
        setItems(
          response.items.map((dto) => ({
            assessmentId: dto.assessment_id,
            title: dto.title,
            startedLabel: dayjs(dto.created_at).format('MMM D, YYYY'),
            lastModifiedBy: dto.last_modified_by,
            status: dto.status,
            completionPct: Math.round(
              (Math.min(dto.current_step, TOTAL_STEPS) / TOTAL_STEPS) * 100,
            ),
            currentStep: Math.min(dto.current_step, TOTAL_STEPS),
            totalSteps: TOTAL_STEPS,
            isApproved: dto.step2_status === 'approved',
            flowStep: deriveAssessmentFlowStep(dto),
          })),
        );
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load assessments');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetch();

    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId, projectId]);

  return { items, loading, error };
}
